/**
 * O seletor no topo de um grupo do menu (escola, hotel, hospital, condomínio, loja): uma lista
 * de escolha pequena que pede ao servidor o submenu do sítio escolhido e o entrega ao casco,
 * que troca os sub-itens desse grupo. Cada seletor (`EscolaSeletor`, `HotelSeletor`…) é isto
 * com o endereço, o ícone, os textos e o `track` dele.
 *
 * Vive em dois sítios do casco: no menu largo (≈ 225 px de largura) e no cartão flutuante do
 * menu minimizado (256 px). Com um só sítio para escolher não aparece.
 */
import React, { useEffect, useState } from "react";
import { usePage } from "@inertiajs/react";
import { Icon } from "@iconify/react";
import { Seleccao } from "@/Logigate/ui";
import req from "@/utils/req";
import { showPop } from "@/utils/Pops";

export interface SitioDoGrupo {
    id: number;
    nome: string;
}

export interface SeletorDoGrupoProps<T extends SitioDoGrupo> {
    itens: T[];
    selecionadoId?: number | null;
    /** O caminho do submenu, a seguir ao `base_path` (ex.: `/hotelaria/menu/3`). */
    caminhoMenu: (id: string) => string;
    /** Ícone do Iconify, à esquerda do nome. */
    icone: string;
    /** Para os leitores de ecrã («Escola», «Hotel»…). */
    rotulo: string;
    placeholder: string;
    nadaEncontrado: string;
    /** O aviso quando o servidor não devolve o menu e não diz porquê. */
    textoErro: string;
    /** Guarda a escolha neste aparelho, nesta chave do `localStorage`. */
    chaveGuardada?: string;
    /** Ao montar, se a escolha guardada não for a que veio do servidor, pede logo o menu dela. */
    pedirGuardadaAoMontar?: boolean;
    /** Quando a pessoa escolhe (o `track`; não corre no pedido silencioso ao montar). */
    aoEscolher?: (item: T) => void;
    /** Com o submenu novo (e a resposta inteira do servidor, para quem precisa de mais do que o menu). */
    onTrocar?: (item: T, menu: any[], resposta: Record<string, any>) => void;
}

function lerGuardada(chave?: string): string | null {
    if (!chave) {
        return null;
    }
    try {
        return localStorage.getItem(chave);
    } catch {
        return null;
    }
}

export function SeletorDoGrupo<T extends SitioDoGrupo>({
    itens,
    selecionadoId,
    caminhoMenu,
    icone,
    rotulo,
    placeholder,
    nadaEncontrado,
    textoErro,
    chaveGuardada,
    pedirGuardadaAoMontar,
    aoEscolher,
    onTrocar,
}: SeletorDoGrupoProps<T>) {
    const { props } = usePage<any>();
    const [aCarregar, setACarregar] = useState(false);
    const [actual, setActual] = useState<string>(selecionadoId?.toString() || "");

    const trocar = async (valor: string | null, silencioso = false) => {
        if (!valor) {
            return;
        }
        const item = itens.find((i) => i.id === parseInt(valor));
        if (!item) {
            return;
        }
        if (!silencioso) {
            aoEscolher?.(item);
        }
        setACarregar(true);
        setActual(valor);
        if (chaveGuardada) {
            try {
                localStorage.setItem(chaveGuardada, valor);
            } catch {
                // Sem localStorage a escolha vale só até recarregar.
            }
        }

        // O `req` não rejeita: um erro de rede ou do servidor chega com `estado` ≠ "ok".
        const resposta = await req().get(props.base_path + caminhoMenu(valor));
        if (resposta?.data?.estado === "ok") {
            onTrocar?.(item, resposta.data.menu, resposta.data);
        } else {
            showPop("", resposta?.data?.texto || textoErro, "error");
        }
        setACarregar(false);
    };

    // A escolha guardada neste aparelho ganha à que vem do servidor (que é sempre a primeira).
    useEffect(() => {
        const guardada = lerGuardada(chaveGuardada);
        if (guardada && itens.find((i) => i.id === parseInt(guardada))) {
            setActual(guardada);
            if (pedirGuardadaAoMontar && parseInt(guardada) !== selecionadoId) {
                trocar(guardada, true);
            }
        }
        // Só ao montar, como antes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!itens || itens.length <= 1) {
        return null;
    }

    return (
        <div role="group" aria-label={rotulo}>
            <Seleccao
                tamanho="sm"
                value={actual}
                onChange={(valor) => {
                    // Escolher o mesmo outra vez não pede nada (o Select de antes desmarcava).
                    if (valor && valor !== actual) {
                        trocar(valor);
                    }
                }}
                opcoes={itens.map((i) => ({ value: i.id.toString(), label: i.nome }))}
                placeholder={placeholder}
                nadaEncontrado={nadaEncontrado}
                pesquisavel={itens.length > 6}
                aCarregar={aCarregar}
                disabled={aCarregar}
                icone={<Icon icon={icone} />}
                className="border-zinc-200 bg-zinc-50 font-medium text-zinc-800 hover:border-zinc-300 hover:bg-white dark:border-white/10 dark:bg-white/[0.04] dark:text-zinc-100 dark:hover:bg-white/[0.07]"
            />
        </div>
    );
}
